<template>
  <!-- Borradores por revisar (filtro opcional por `module`). Genérico. -->
  <div class="dl">
    <p v-if="loading" class="dl-muted">{{ $t('assistant.loading') }}</p>
    <p v-else-if="error" class="dl-muted">{{ error }}</p>

    <div v-else-if="!drafts.length" class="dl-empty">
      <span class="dl-empty__ic"><PhCheckCircle :size="28" weight="duotone" /></span>
      <p class="dl-empty__title">{{ $t('assistant.pending.emptyTitle') }}</p>
      <p class="dl-muted">{{ $t('assistant.pending.empty') }}</p>
    </div>

    <template v-else>
      <p class="dl-lead">{{ $t('assistant.pending.lead', { n: drafts.length }, drafts.length) }}</p>
      <ul class="dl-list">
        <li v-for="d in drafts" :key="d.id">
          <button type="button" class="dl-item" @click="$emit('open', d)">
            <span class="dl-kind" :class="`dl-kind--${d.kind || 'other'}`">
              <component :is="kindIcon(d.module, d.kind)" :size="20" weight="duotone" />
            </span>

            <span class="dl-main">
              <span class="dl-row">
                <span class="dl-name">{{ titleOf(d) }}</span>
                <span class="dl-state" :class="isReady(d) ? 'is-ready' : 'is-missing'">
                  {{ isReady(d) ? $t('assistant.pending.ready') : $t('assistant.pending.missing', { n: missingCount(d) }, missingCount(d)) }}
                </span>
              </span>
              <span v-if="summary(d)" class="dl-meta">{{ summary(d) }}</span>
              <span v-if="!isReady(d) && questionText(d)" class="dl-q">{{ questionText(d) }}</span>
              <span class="dl-file">
                <PhFileText :size="12" />
                <span :title="d.file_name">{{ shortFileName(d.file_name) }}</span>
                <span v-if="d.warnings?.length" class="dl-warn">
                  <PhWarning :size="12" weight="fill" /> {{ $t('assistant.pending.warnings', { n: d.warnings.length }, d.warnings.length) }}
                </span>
              </span>
            </span>

            <PhCaretRight :size="16" class="dl-go" />
          </button>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { PhCaretRight, PhWarning, PhFileText, PhCheckCircle } from '@phosphor-icons/vue';
import { useAssistant } from '@/composables/useAssistant';
import { kindIcon } from '@/composables/useAssistantDock';
import { shortFileName } from '@/utils/shortFileName';

const UNITS = { kWh: 'kWh', m3: 'm³', 'm³': 'm³', L: 'L' };

export default {
  name: 'DraftList',
  components: { PhCaretRight, PhWarning, PhFileText, PhCheckCircle },
  props: {
    module: { type: String, default: null },
  },
  emits: ['open', 'count'],
  setup(props, { emit }) {
    const { t, te, locale } = useI18n();
    const api = useAssistant();
    const drafts = ref([]);
    const loading = ref(true);
    const error = ref('');

    const titleOf = (d) => {
      const k = `assistant.modules.${d.module}.kinds.${d.kind}`;
      return te(k) ? t(k) : t('assistant.draft.genericTitle');
    };
    // En la lista, `question` puede venir como texto o como objeto.
    const questionText = (d) => (typeof d.question === 'string' ? d.question : d.question?.text) || '';
    const missingCount = (d) => (d.missing || []).length || (d.question ? 1 : 0);
    const isReady = (d) => !missingCount(d);

    // "346 kWh · 9 sep 2026"
    const summary = (d) => {
      const r = d.reading || {};
      const parts = [];
      // Planillas: "7 filas"
      if (r.summary?.total != null) {
        const n = r.summary.total;
        return t(r.doc_type === 'consumption_log' ? 'assistant.sheet.sumReadings' : 'assistant.sheet.sumRows', { n }, n);
      }
      if (r.consumption != null) {
        parts.push(`${new Intl.NumberFormat(locale.value, { maximumFractionDigits: 2 }).format(r.consumption)} ${UNITS[r.unit] || r.unit || ''}`.trim());
      }
      if (r.date) {
        parts.push(new Date(`${String(r.date).slice(0, 10)}T12:00:00`).toLocaleDateString(locale.value, { day: 'numeric', month: 'short', year: 'numeric' }));
      }
      if (r.provider) parts.push(r.provider);
      return parts.join(' · ');
    };

    const load = async () => {
      loading.value = true; error.value = '';
      const res = await api.listDrafts({ module: props.module || undefined, status: 'pending', limit: 50 });
      loading.value = false;
      if (res.ok) {
        drafts.value = res.data?.drafts || [];
        emit('count', res.data?.total ?? drafts.value.length);
      } else {
        error.value = res.message || t('assistant.errors.generic');
      }
    };
    onMounted(load);

    return { drafts, loading, error, kindIcon, shortFileName, titleOf, questionText, missingCount, isReady, summary, load };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/components/assistant/draft-list";
</style>
